import { ReactNode, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { GRAY_LIGHT_SECONDARY, PRIMARY, RED } from 'constants/colors';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

const StyledDialog = styled.div`
  min-width: 360px;

  .MuiDialogTitle-root {
    border-bottom: 1px solid ${GRAY_LIGHT_SECONDARY};
    margin-bottom: 10px;
  }

  .MuiDialogContent-root {
    margin-bottom: 20px;
    font-size: 16px;
    font-family: 'SourceSansPro-Regular';
    white-space: pre-line;

    p {
      margin: 0;
    }
  }

  .MuiDialogTitle-root {
    font-size: 18px;
  }

  .MuiDialogActions-root {
    padding: 0 24px 24px;
  }

  &.info {
    .MuiDialogTitle-root {
      border-color: ${GRAY_LIGHT_SECONDARY};
      color: ${PRIMARY};
    }
  }

  &.warning {
    .MuiDialogTitle-root {
      border-color: ${GRAY_LIGHT_SECONDARY};
      color: ${PRIMARY};
    }
  }

  &.error {
    .MuiDialogTitle-root {
      border-color: ${RED};
      color: ${RED};
    }
  }
`;

type Props = {
  title?: string | null;
  type?: 'error' | 'warning' | 'info';
  handleAction: (isConfirmed: boolean) => void | Promise<boolean>;
  cancelBtnText?: string;
  confirmBtnText?: string;
  children: ReactNode | ReactNode[];
  disableConfirm?: boolean;
};

export default function ConfirmationDialog(props: Props) {
  const { t } = useTranslation('common');
  const {
    title,
    handleAction,
    cancelBtnText = t('cancel'),
    confirmBtnText = t('ok'),
    children,
    type,
    disableConfirm
  } = props;
  const [isOpen, setIsOpen] = useState(true);
  const handleConfirm = async () => {
    const shouldClose = await handleAction(true);
    shouldClose && setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
    handleAction(false);
  };

  return (
    <Dialog aria-labelledby="confirmation-dialog" open={isOpen}>
      <StyledDialog className={classNames(type)}>
        {title && <DialogTitle>{title}</DialogTitle>}
        {children && <DialogContent>{children}</DialogContent>}
        <DialogActions>
          <Button onClick={handleCancel} color="primary" variant="outlined">
            {cancelBtnText}
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
            disabled={disableConfirm}
          >
            {confirmBtnText}
          </Button>
        </DialogActions>
      </StyledDialog>
    </Dialog>
  );
}
